import { useState, useEffect, useCallback } from "react";
import api from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import {
  Clock,
  CheckCircle,
  XCircle,
  UserPlus,
  Shield,
  X,
} from "lucide-react";

interface AccessRequestItem {
  id: string;
  name: string;
  email: string;
  company: string | null;
  message: string | null;
  status: string;
  created_at: string | null;
  reviewed_at: string | null;
}

export default function AccessRequestsPage() {
  const { token } = useAuth();
  const [requests, setRequests] = useState<AccessRequestItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [approveModal, setApproveModal] = useState<AccessRequestItem | null>(
    null
  );
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState("analyst");
  const [actionLoading, setActionLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchRequests = useCallback(async () => {
    try {
      const { data } = await api.get("/api/auth/access-requests", { headers });
      setRequests(data);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  async function handleApprove() {
    if (!approveModal || !newPassword) return;
    setActionLoading(true);
    try {
      await api.post(
        `/api/auth/access-requests/${approveModal.id}/approve`,
        { role: newRole, password: newPassword },
        { headers }
      );
      setApproveModal(null);
      setNewPassword("");
      setNewRole("analyst");
      fetchRequests();
    } catch {
      // error handling
    } finally {
      setActionLoading(false);
    }
  }

  async function handleDeny(id: string) {
    try {
      await api.post(`/api/auth/access-requests/${id}/deny`, {}, { headers });
      fetchRequests();
    } catch {
      // error handling
    }
  }

  const pending = requests.filter((r) => r.status === "pending");
  const processed = requests.filter((r) => r.status !== "pending");

  function fmtDate(v: string | null) {
    if (!v) return "--";
    return new Date(v).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-8 py-12">
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-grove-300 border-t-forest-700 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-8 py-10">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-lg bg-forest-700/10 flex items-center justify-center">
          <Shield className="w-5 h-5 text-forest-700" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-semibold text-bark-800">
            Access Requests
          </h1>
          <p className="text-sm text-bark-500">
            Manage platform access for new users
          </p>
        </div>
      </div>

      {/* Pending requests */}
      <div className="mb-10">
        <h2 className="text-sm font-semibold text-bark-500 uppercase tracking-wider mb-4">
          Pending ({pending.length})
        </h2>
        {pending.length === 0 ? (
          <div className="bg-white rounded-xl border border-[#E8E4DA] p-8 text-center">
            <Clock className="w-8 h-8 text-grove-300 mx-auto mb-3" />
            <p className="text-sm text-bark-500">No pending requests</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pending.map((r) => (
              <div
                key={r.id}
                className="bg-white rounded-xl border border-[#E8E4DA] p-5 flex items-start justify-between gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-bark-800 text-sm">
                      {r.name}
                    </span>
                    <span className="text-xs text-[#9C9A90]">
                      {fmtDate(r.created_at)}
                    </span>
                  </div>
                  <p className="text-sm text-bark-500">{r.email}</p>
                  {r.company && (
                    <p className="text-xs text-[#9C9A90] mt-0.5">{r.company}</p>
                  )}
                  {r.message && (
                    <p className="text-xs text-bark-500 mt-2 bg-grove-100 px-3 py-2 rounded-lg">
                      "{r.message}"
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => setApproveModal(r)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-forest-700 text-white text-xs font-semibold rounded-lg hover:bg-forest-600 transition-colors"
                  >
                    <UserPlus className="w-3.5 h-3.5" />
                    Approve
                  </button>
                  <button
                    onClick={() => handleDeny(r.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-red-200 text-red-600 text-xs font-semibold rounded-lg hover:bg-red-50 transition-colors"
                  >
                    <XCircle className="w-3.5 h-3.5" />
                    Deny
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Processed requests */}
      {processed.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-bark-500 uppercase tracking-wider mb-4">
            History ({processed.length})
          </h2>
          <div className="space-y-2">
            {processed.map((r) => (
              <div
                key={r.id}
                className="bg-white/60 rounded-lg border border-[#E8E4DA] p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-3 min-w-0">
                  {r.status === "approved" ? (
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                  )}
                  <div>
                    <span className="text-sm font-medium text-bark-700">
                      {r.name}
                    </span>
                    <span className="text-xs text-[#9C9A90] ml-2">
                      {r.email}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span
                    className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      r.status === "approved"
                        ? "bg-green-50 text-green-700"
                        : "bg-red-50 text-red-600"
                    }`}
                  >
                    {r.status}
                  </span>
                  <span className="text-xs text-[#9C9A90]">
                    {fmtDate(r.reviewed_at)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Approve modal */}
      {approveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl border border-[#E8E4DA] w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-lg font-semibold text-bark-800">
                Approve Access
              </h3>
              <button
                onClick={() => setApproveModal(null)}
                className="p-1 hover:bg-grove-100 rounded-lg transition-colors"
              >
                <X className="w-4 h-4 text-bark-500" />
              </button>
            </div>

            <div className="mb-5 bg-grove-100 rounded-lg p-3">
              <p className="text-sm font-medium text-bark-800">
                {approveModal.name}
              </p>
              <p className="text-xs text-bark-500">{approveModal.email}</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-bark-700 mb-1.5">
                  Role
                </label>
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-grove-300 rounded-lg text-sm text-bark-800"
                >
                  <option value="analyst">Analyst (view + scenario)</option>
                  <option value="broker">Broker (full access)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-bark-700 mb-1.5">
                  Set Password
                </label>
                <input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-grove-300 rounded-lg text-sm text-bark-800 placeholder-[#9C9A90]"
                  placeholder="Temporary password for the user"
                />
                <p className="text-xs text-[#9C9A90] mt-1">
                  Share this password with the user securely
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setApproveModal(null)}
                className="px-4 py-2 text-sm text-bark-500 hover:text-bark-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={!newPassword || actionLoading}
                className="px-4 py-2 bg-forest-700 text-white text-sm font-semibold rounded-lg hover:bg-forest-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {actionLoading ? "Creating..." : "Create Account"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
